import React, { useState } from 'react'
import { Flex, Icon } from '@chakra-ui/react'
import { FiInfo } from 'react-icons/fi'

import { useClinic } from '~contexts/clinic-context'
import { getClientFullName } from '~utils/helpers'
import { useSearchClients } from '~utils/hooks/use-search-clients'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { useCreatePaymentLinkMutation } from '~graphql/generated/graphql'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedInfoText from '~components/shared/texts/shared-info-text'
import SharedHelpText from '~components/shared/form/shared-help-text'
import { FtsSelect } from '~components/shared/form/fts-select'
import SharedTextarea from '~components/shared/form/shared-textarea'
import SharedNumberInput from '~components/shared/form/shared-number-input'
import ClientInfoTooltip from '~components/shared/client/client-info-tooltip'

const MIN_AMOUNT_CENTS = 50
const MAX_DESCRIPTION_LENGTH = 500

interface Props {
    isOpen: boolean
    onClose: () => void
    /**
     * When provided (inbox), the link is created for this client and the
     * client search is hidden. Omit it (payments page) to pick a client.
     */
    clientId?: string | null
    /** Appointment to attach, e.g. the one the chat was opened from. */
    appointmentId?: string
    /** Called with the checkout URL after a link is created. */
    onCreated?: (url: string) => void
}

/**
 * Creates a staff-generated text-to-pay link. Render gated on `isOpen`
 * (`{isOpen && <PaymentLinkModal .../>}`) so the client search only runs
 * while the modal is open and form state resets between opens.
 */
export default function PaymentLinkModal({
    isOpen,
    onClose,
    clientId,
    appointmentId,
    onCreated,
}: Props) {
    const { clinic } = useClinic()
    const toasts = useToastFeedback(
        'Payment link created.',
        'Error creating payment link.',
    )

    const [clientQuery, setClientQuery] = useState('')
    const [selectedClientId, setSelectedClientId] = useState<string | null>(
        null,
    )
    const [amountDollars, setAmountDollars] = useState(0)
    const [description, setDescription] = useState('')

    const {
        clientResults,
        loading: isSearchLoading,
        hasMore: hasMoreSearchResults,
        setPage: setSearchPage,
    } = useSearchClients({
        clinic,
        query: clientQuery,
        shouldAccumulatePages: true,
    })

    const effectiveClientId = clientId ?? selectedClientId
    const amountCents = Math.round((amountDollars || 0) * 100)
    const trimmedDescription = description.trim()

    const isValid =
        !!clinic?.id &&
        !!effectiveClientId &&
        amountCents >= MIN_AMOUNT_CENTS &&
        trimmedDescription.length > 0 &&
        trimmedDescription.length <= MAX_DESCRIPTION_LENGTH

    const [createPaymentLink, { loading }] = useCreatePaymentLinkMutation({
        onCompleted: async data => {
            const url =
                data.createPaymentLink.stripe_checkout_session_url

            if (!url) {
                toasts.onError(
                    'The payment was created but no link was returned.',
                )
                return
            }

            try {
                await navigator.clipboard.writeText(url)
                toasts.onCompleted(
                    'Payment link created and copied to clipboard.',
                )
            } catch {
                toasts.onCompleted(
                    'Payment link created. Copy it from the payments table.',
                )
            }

            onCreated?.(url)
            onClose()
        },
        onError: error => toasts.onError(error.message),
    })

    function handleConfirm() {
        if (!clinic?.id || !effectiveClientId || !isValid) {
            return
        }

        createPaymentLink({
            variables: {
                data: {
                    clinic: clinic.id,
                    client: effectiveClientId,
                    amount: amountCents,
                    description: trimmedDescription,
                    ...(appointmentId ? { appointment: appointmentId } : {}),
                },
            },
        })
    }

    return (
        <SharedModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            onConfirm={handleConfirm}
            confirmText="Create link"
            title="New payment link"
            isConfirmDisabled={!isValid}
            isLoading={loading}
            skipCloseOnConfirm
        >
            <SharedStack gap={6}>
                {!clientId && (
                    <SharedStack>
                        <SharedInfoText>Client</SharedInfoText>
                        <FtsSelect
                            options={clientResults}
                            filteredOptions={clientResults}
                            selectedOptions={clientResults.filter(
                                r => r.client?.id === selectedClientId,
                            )}
                            setSelectedOptions={selected => {
                                const last =
                                    selected[selected.length - 1]
                                setSelectedClientId(
                                    last?.client?.id ?? null,
                                )
                            }}
                            displayTransformer={o => (
                                <ClientInfoTooltip client={o.client}>
                                    <Flex alignItems="center">
                                        {getClientFullName(o.client)}
                                        <Icon as={FiInfo} pl={1} />
                                    </Flex>
                                </ClientInfoTooltip>
                            )}
                            setSearch={setClientQuery}
                            placeholder="Search for clients"
                            isLoading={isSearchLoading}
                            hasMore={hasMoreSearchResults}
                            onScrolledToBottom={() =>
                                setSearchPage(page => page + 1)
                            }
                        />
                    </SharedStack>
                )}
                <SharedStack>
                    <SharedInfoText>Amount</SharedInfoText>
                    <SharedNumberInput
                        value={amountDollars}
                        onChange={(_valueAsString, valueAsNumber) =>
                            setAmountDollars(
                                isNaN(valueAsNumber) ? 0 : valueAsNumber,
                            )
                        }
                        min={0}
                        precision={2}
                    />
                    <SharedHelpText mb={0}>
                        Minimum $0.50. Your clinic&apos;s standard processing
                        fees may be added at checkout.
                    </SharedHelpText>
                </SharedStack>
                <SharedStack>
                    <SharedInfoText>Description</SharedInfoText>
                    <SharedTextarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="e.g. Dental cleaning deposit"
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        rows={2}
                    />
                    <SharedHelpText mb={0}>
                        Shown to the client at checkout.{' '}
                        {description.length}/{MAX_DESCRIPTION_LENGTH}
                    </SharedHelpText>
                </SharedStack>
                <SharedHelpText mb={0}>
                    Links are single-use and expire 24 hours after creation if
                    unpaid.
                </SharedHelpText>
            </SharedStack>
        </SharedModal>
    )
}
